/**
 * Feel Great V2 — Design Tokens (Foundation)
 *
 * Source of truth: Figma "Feel Great" file, node 87:2
 * (🎨 Design Tokens — Foundation). Values are copied VERBATIM from Figma
 * Variables & Styles — do not hand-edit or compute; change them in Figma and
 * re-sync. Token names mirror the Figma paths (color/{family}/{shade},
 * spacing/{n}, radius/{n}, shadow/{level}, plus the typography scale).
 */

// ─── 01 · Color · 5 families × 10 shades + neutrals ─────────────────────────
export const color = {
    blue: {
        50: '#ebf2f9',
        100: '#c2d8ee',
        200: '#99bee3',
        300: '#5a8fc3',
        400: '#2e6fab',
        500: '#0a5290',
        600: '#003b6f',
        700: '#002d57',
        800: '#001f3f',
        900: '#001228'
    },
    gray: {
        50: '#f8fafc',
        100: '#f4f8fb',
        200: '#f0f2f5',
        300: '#e2e8f0',
        400: '#ccdeef',
        500: '#99bcdf',
        600: '#6b8cae',
        700: '#4a6580',
        800: '#384250',
        900: '#1a202c'
    },
    red: {
        50: '#fff5f7',
        100: '#ffe7eb',
        200: '#ffcdd4',
        300: '#ffb0bc',
        400: '#ff8a9b',
        500: '#fe5d7c',
        600: '#e83a5f',
        700: '#c41f47',
        800: '#9c0e32',
        900: '#7a0024'
    },
    green: {
        50: '#f0fdf4',
        100: '#dcfce7',
        200: '#bbf7d0',
        300: '#86efac',
        400: '#4ade80',
        500: '#22c55e',
        600: '#16a34a',
        700: '#15803d',
        800: '#166534',
        900: '#14532d'
    },
    yellow: {
        50: '#fefce8',
        100: '#fef9c3',
        200: '#fef08a',
        300: '#fde047',
        400: '#facc15',
        500: '#eab308',
        600: '#ca8a04',
        700: '#a16207',
        800: '#854d0e',
        900: '#713f12'
    },
    // orange & cyan are referenced by the semantic layer (Figma node 94:3) but
    // are not shown on the 87:2 foundation board. Values verbatim from 94:3.
    orange: {
        50: '#fff8f0',
        100: '#fff1dc',
        200: '#ffe0b2',
        300: '#ffbf3a',
        400: '#ffa001',
        500: '#ff820e',
        600: '#e86900',
        700: '#c45200',
        800: '#9c3d00',
        900: '#7a2900'
    },
    cyan: {
        50: '#eff8ff',
        100: '#dbeafe',
        200: '#bfdbfe',
        300: '#93c5fd',
        400: '#60a5fa',
        500: '#079ff4',
        600: '#0284c7',
        700: '#0369a1',
        800: '#075985',
        900: '#0c4a6e'
    },
    white: '#ffffff',
    black: '#000000'
} as const

// ─── 02 · Typography · 15 styles · family Inter ─────────────────────────────
// weight/size/lineHeight/letterSpacing exactly as Figma text styles.
export interface TypeStyle {
    fontFamily: string
    fontWeight: number
    fontSize: number
    lineHeight: number
    letterSpacing: number
}

const inter = 'Inter'

export const typography = {
    headline: {
        h1: { fontFamily: inter, fontWeight: 700, fontSize: 32, lineHeight: 44, letterSpacing: -1.2 },
        h2: { fontFamily: inter, fontWeight: 700, fontSize: 24, lineHeight: 32, letterSpacing: -0.75 },
        h3: { fontFamily: inter, fontWeight: 600, fontSize: 20, lineHeight: 28, letterSpacing: -0.6 },
        h4: { fontFamily: inter, fontWeight: 600, fontSize: 16, lineHeight: 24, letterSpacing: -0.5 },
        h5: { fontFamily: inter, fontWeight: 600, fontSize: 14, lineHeight: 20, letterSpacing: -0.5 }
    },
    body: {
        b1: { fontFamily: inter, fontWeight: 400, fontSize: 18, lineHeight: 28, letterSpacing: 0 },
        b2: { fontFamily: inter, fontWeight: 400, fontSize: 16, lineHeight: 24, letterSpacing: 0 },
        b3: { fontFamily: inter, fontWeight: 400, fontSize: 14, lineHeight: 20, letterSpacing: 0 },
        b4: { fontFamily: inter, fontWeight: 400, fontSize: 12, lineHeight: 16, letterSpacing: 0 },
        b5: { fontFamily: inter, fontWeight: 400, fontSize: 10, lineHeight: 14, letterSpacing: 0 }
    },
    label: {
        l1: { fontFamily: inter, fontWeight: 600, fontSize: 18, lineHeight: 28, letterSpacing: 0 },
        l2: { fontFamily: inter, fontWeight: 600, fontSize: 16, lineHeight: 24, letterSpacing: 0 },
        l3: { fontFamily: inter, fontWeight: 500, fontSize: 14, lineHeight: 20, letterSpacing: 0 },
        l4: { fontFamily: inter, fontWeight: 500, fontSize: 12, lineHeight: 16, letterSpacing: 0 },
        l5: { fontFamily: inter, fontWeight: 500, fontSize: 10, lineHeight: 14, letterSpacing: 0 }
    }
} as const satisfies Record<string, Record<string, TypeStyle>>

// ─── 03 · Spacing · 9 values · base unit 4px ────────────────────────────────
export const spacing = {
    4: 4,
    8: 8,
    12: 12,
    16: 16,
    20: 20,
    24: 24,
    28: 28,
    32: 32,
    48: 48
} as const

// ─── 04 · Radius · 5 values ─────────────────────────────────────────────────
export const radius = {
    3: 3,
    6: 6,
    8: 8,
    12: 12,
    16: 16
} as const

// ─── 05 · Shadow · 5 elevation levels ───────────────────────────────────────
// 8-digit hex keeps the exact Figma color+alpha (base #0F141A) without
// converting to rgba(). Order matches Figma effect stacks.
export const shadow = {
    sm: '0px 1px 2px 0px #0F141A0D',
    md: '0px 4px 8px 0px #0F141A0F, 0px 2px 4px -2px #0F141A0A',
    lg: '0px 12px 24px -2px #0F141A14, 0px 4px 8px -4px #0F141A0D',
    xl: '0px 24px 48px -4px #0F141A1F, 0px 8px 16px -8px #0F141A0F',
    inner: 'inset 0px 2px 4px 0px #0F141A0F'
} as const

// ─── 06 · Layout Grid · 3 breakpoints ───────────────────────────────────────
export const layoutGrid = {
    desktop: { width: 1440, columns: 12, margin: 144, gutter: 24 },
    tablet: { width: 1366, columns: 12, margin: 83, gutter: 24 },
    mobile: { width: 440, columns: 4, margin: 20, gutter: 16 }
} as const

export const tokens = { color, typography, spacing, radius, shadow, layoutGrid } as const
